import { COPY } from '@/shared/constants/copies';
import { DOCS_LINKS } from '@/shared/constants/docs';
import { PlanType } from '@/shared/services/payment/payment.types';
import { formatWholeUsd } from '@/shared/utils/format-money';

import type { CopyFeature } from '@/types/copy';

import { PlanFeatures, PricingPlans } from '../types';
import { OFFER_LETTER_LIMITS, PLAN_CREDITS } from './plan-limits';

/**
 * Non-copy scaffolding for a feature line, keyed by its registry `text`.
 *
 * Keyed rather than positional so reordering or inserting a feature in the registry
 * cannot silently attach the wrong link or number to a line.
 */
type FeatureExtras = { limit?: string; href?: string };

function buildFeatures(
	features: readonly CopyFeature[],
	extras: Record<string, FeatureExtras>
): PlanFeatures {
	return features.map((feature) => {
		const { limit, href } = extras[feature.text] ?? {};
		const fill = (value: string) =>
			limit === undefined ? value : value.replaceAll('{limit}', limit);

		const text = fill(feature.text);
		const tooltip = feature.tooltip ? fill(feature.tooltip) : undefined;

		return tooltip || href ? { text, tooltip, href } : text;
	});
}

export const FREE_PLAN_FEATURES: PlanFeatures = buildFeatures(COPY.pricing.freeFeatures, {
	'AI agent, Slack agent': { href: DOCS_LINKS.aiAgent },
	'Careers page': { href: DOCS_LINKS.careersPage },
	'Email candidates': { href: DOCS_LINKS.emailCandidates },
	'Chrome extension': { href: DOCS_LINKS.chromeExtension },
	'{limit} free credit/month': {
		limit: formatWholeUsd(PLAN_CREDITS.free),
		href: DOCS_LINKS.usageAndCredits,
	},
});

export const PRO_PLAN_FEATURES: PlanFeatures = buildFeatures(COPY.pricing.starterFeatures, {
	Analytics: { href: DOCS_LINKS.jobAnalytics },
	'{limit} free credits/month': {
		limit: formatWholeUsd(PLAN_CREDITS.starter),
		href: DOCS_LINKS.usageAndCredits,
	},
	'Add-on credits': { href: DOCS_LINKS.usageAndCredits },
});

export const BUSINESS_PLAN_FEATURES: PlanFeatures = buildFeatures(COPY.pricing.businessFeatures, {
	'Interview scheduling': { href: DOCS_LINKS.interviewScheduling },
	'Interview feedback': { href: DOCS_LINKS.interviewFeedback },
	'Offer letters': {
		href: DOCS_LINKS.offerLetters,
		limit: String(OFFER_LETTER_LIMITS.business),
	},
	Assignments: { href: DOCS_LINKS.assignments },
	'Advanced analytics': { href: DOCS_LINKS.workspaceAnalytics },
	'{limit} free credits/month': {
		limit: formatWholeUsd(PLAN_CREDITS.business),
		href: DOCS_LINKS.usageAndCredits,
	},
});

export const PRO_TIER_FEATURES: PlanFeatures = buildFeatures(COPY.pricing.proFeatures, {
	'Unlimited offer letters': { href: DOCS_LINKS.offerLetters },
	'{limit} free credits/month': {
		limit: formatWholeUsd(PLAN_CREDITS.pro),
		href: DOCS_LINKS.usageAndCredits,
	},
});

export const pricingPlans: PricingPlans = {
	pro_monthly: {
		id: PlanType.PRO_MONTHLY,
		name: COPY.pricing.planNameStarterMonthly,
	},
	pro_yearly: {
		id: PlanType.PRO_YEARLY,
		name: COPY.pricing.planNameStarterYearly,
	},
	unlimited_pro_monthly: {
		id: PlanType.UNLIMITED_PRO_MONTHLY,
		name: COPY.pricing.planNameBusinessMonthly,
	},
	unlimited_pro_yearly: {
		id: PlanType.UNLIMITED_PRO_YEARLY,
		name: COPY.pricing.planNameBusinessYearly,
	},
	premium_pro_monthly: {
		id: PlanType.PREMIUM_PRO_MONTHLY,
		name: COPY.pricing.planNameProMonthly,
	},
	premium_pro_yearly: {
		id: PlanType.PREMIUM_PRO_YEARLY,
		name: COPY.pricing.planNameProYearly,
	},
	free: {
		id: PlanType.FREE,
		name: COPY.pricing.planNameFree,
	},
};
